import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface GroupInputProps {
  selectedGroups: string[];
  existingGroups: string[];
  onChange: (groups: string[]) => void;
}

const GroupInput: React.FC<GroupInputProps> = ({
  selectedGroups,
  existingGroups,
  onChange
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [availableSuggestions, setAvailableSuggestions] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  
  // Ensure selectedGroups is always an array
  const safeSelectedGroups = Array.isArray(selectedGroups) ? selectedGroups : [];
  
  // Debug logging to track values 
  useEffect(() => {
    console.log("GroupInput - selectedGroups:", safeSelectedGroups);
    console.log("GroupInput - existingGroups:", existingGroups);
    
    // Update available suggestions whenever selected groups or existing groups change
    updateSuggestions();
  }, [safeSelectedGroups, existingGroups, inputValue]);

  // Update the suggestions based on current input and selected groups
  const updateSuggestions = () => {
    // Ensure we have valid arrays to prevent errors
    const filtered = (existingGroups || [])
      .filter(group => 
        // When input is empty, show all groups or filter by what user typed
        (inputValue.trim() === '' || 
         group.toLowerCase().includes(inputValue.trim().toLowerCase()))
        // Don't suggest groups that are already selected
        && !safeSelectedGroups.includes(group)
      )
      // Sort alphabetically for better user experience
      .sort((a, b) => a.localeCompare(b));
    
    setAvailableSuggestions(filtered);
    console.log("Updated suggestions:", filtered);
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node) &&
          inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Add a group to the selected list
  const handleAddGroup = async (group: string) => {
    // Skip empty strings
    if (!group.trim()) return;
    
    const groupToAdd = group.trim();

    // Make sure we don't add duplicates and use the safe array
    if (!safeSelectedGroups.includes(groupToAdd)) {
      let createGroupSuccess = true;
      
      // Create a new group in the database if it doesn't exist in existingGroups
      if (!existingGroups.includes(groupToAdd)) {
        try {
          console.log(`Creating new group "${groupToAdd}" in database`);
          
          // Import the API
          const { api } = await import('../../services/api.dynamic');
          
          // Create a new formal group with both required fields
          await api.createGroup({
            name: groupToAdd,
            description: `Group for ${groupToAdd}`
          });
          
          console.log(`Successfully created new group "${groupToAdd}" in database`);
        } catch (error) {
          createGroupSuccess = false;
          console.error(`Failed to create new group "${groupToAdd}" in database:`, error);
          const errorMsg = error instanceof Error ? error.message : String(error);
          console.error(`Error details: ${errorMsg}`);
          // Optionally: display an error to the user here
          // For now, we prevent adding the group to the contact if creation fails
          // Alternatively, we could allow adding but warn the user
        }
      }
      
      // Only update the contact's groups if the group exists or was successfully created
      if (createGroupSuccess) {
        const updatedGroups = [...safeSelectedGroups, groupToAdd];
        console.log("Adding group:", groupToAdd, "Updated groups:", updatedGroups);
        onChange(updatedGroups);
      }
    }
    
    // Clear input and hide suggestions regardless of success/failure
    setInputValue('');
    setShowSuggestions(false);
  };

  const handleRemoveGroup = (groupToRemove: string) => {
    const updatedGroups = safeSelectedGroups.filter(group => group !== groupToRemove);
    console.log("Removing group:", groupToRemove, "Updated groups:", updatedGroups);
    onChange(updatedGroups);
  };

  // Force show suggestions when input is focused 
  const handleInputFocus = () => {
    // Always show the suggestions dropdown when the input is focused
    setShowSuggestions(true);
    
    // Force an update of suggestions
    updateSuggestions();
    
    console.log("Input focused - showing suggestions. Available groups:", 
      (existingGroups || []).filter(g => !safeSelectedGroups.includes(g)));
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      if (availableSuggestions.length > 0) {
        console.log("Adding suggested group:", availableSuggestions[0]);
        handleAddGroup(availableSuggestions[0]);
      } else {
        console.log("Adding new group:", inputValue.trim());
        handleAddGroup(inputValue.trim());
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    } else if (e.key === 'ArrowDown' && showSuggestions && availableSuggestions.length > 0) {
      // Could implement keyboard navigation through suggestions
      e.preventDefault();
    }
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            const value = e.target.value;
            setInputValue(value);
            // Always show suggestions when input changes
            setShowSuggestions(true);
            // Update suggestions will be triggered by the effect that depends on inputValue
          }}
          onKeyDown={handleInputKeyDown}
          onFocus={handleInputFocus}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
          placeholder="Type to create a new group or select from existing"
        />
        
        {/* Suggestions dropdown - always render container with conditional display */}
        <div 
          ref={suggestionsRef}
          className={`absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto ${
            showSuggestions ? 'block' : 'hidden'
          }`}
          style={{minHeight: '40px'}}
        >
          {availableSuggestions.length > 0 ? (
            availableSuggestions.map(suggestion => (
              <button
                key={suggestion}
                className="w-full px-4 py-2 text-left bg-white hover:bg-blue-50 focus:outline-none focus:bg-blue-50"
                onClick={() => handleAddGroup(suggestion)}
              >
                {suggestion}
              </button>
            ))
          ) : (
            <div className="px-4 py-2 text-gray-500 italic">
              {inputValue.trim() ? 
                `No matching groups - press Enter to create "${inputValue.trim()}"` : 
                'Type to create a new group or select from existing groups'}
            </div>
          )}
        </div>
      </div>

      {/* Selected groups */}
      <div className="flex flex-wrap gap-2">
        {safeSelectedGroups.map(group => (
          <span
            key={group}
            className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-sm"
          >
            {group}
            <button
              onClick={(e) => {
                e.preventDefault(); // Prevent form submission
                e.stopPropagation(); // Stop event propagation
                handleRemoveGroup(group);
              }}
              className="hover:text-blue-900"
            >
              <X size={14} />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};

export default GroupInput;